import './HeroRecommender.css';
import type { Hero } from '../types';
import type { CounterReasonEntry, SynergyReasonEntry } from '../utils/winRateDelta';
import { heroDisplayName } from '../../tierlist/utils/heroDisplayName';
import { CounterReasonRow, SynergyReasonRow } from './recommendationReasonRows';

const RESIDUAL_EPS = 1e-6;

export interface RecommendationReasons {
  baseDelta: number;
  counterReasons: CounterReasonEntry[];
  synergyReasons: SynergyReasonEntry[];
  residualDelta: number;
}

interface RecommendationCardProps {
  hero: Hero;
  winRateDelta: number;
  reasons: RecommendationReasons;
  pickLabel: string;
  onSelect: () => void;
}

export function RecommendationCard({
  hero,
  winRateDelta,
  reasons,
  pickLabel,
  onSelect,
}: RecommendationCardProps) {
  const { baseDelta, counterReasons, synergyReasons, residualDelta } = reasons;
  const showResidual = Math.abs(residualDelta) > RESIDUAL_EPS;
  const mainName = heroDisplayName(hero.id, hero.name);

  return (
    <div className="recommendation-card">
      <div className="recommendation-main">
        <div className="hero-thumb-wrapper">
          <img src={hero.head_url || ''} alt={mainName} className="hero-thumb" />
          <div className={`win-rate-badge ${winRateDelta >= 0 ? 'positive' : 'negative'}`}>
            {winRateDelta >= 0 ? '+' : ''}
            {(winRateDelta * 100).toFixed(1)}%
          </div>
        </div>
        <div className="hero-info">
          <span className="hero-name">{mainName}</span>
        </div>
        <button
          type="button"
          className="select-btn"
          onClick={onSelect}
          aria-label={`${pickLabel}に追加`}
        >
          {pickLabel}
        </button>
      </div>

      <div className="reasons-list">
        <div className={`reason-item base ${baseDelta >= 0 ? 'positive' : 'negative'}`}>
          <span className="reason-label">基本勝率</span>
          <span className="reason-value">
            {baseDelta >= 0 ? '+' : ''}
            {(baseDelta * 100).toFixed(1)}%
          </span>
        </div>

        {counterReasons.map((reason, idx) => (
          <CounterReasonRow key={`c-${idx}`} reason={reason} />
        ))}

        {synergyReasons.map((reason, idx) => (
          <SynergyReasonRow key={`s-${idx}`} reason={reason} />
        ))}

        {showResidual && (
          <div className={`reason-item residual ${residualDelta >= 0 ? 'positive' : 'negative'}`}>
            <span className="reason-label">その他（相互作用・クランプ）</span>
            <span className="reason-value">
              {residualDelta >= 0 ? '+' : ''}
              {(residualDelta * 100).toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
